// Browser-side download manager. Owns the queue of in-flight bundle jobs,
// drives their progress callbacks, holds the resulting blobs for download/
// re-download, and warns the user if they try to navigate away while jobs
// are still running.
//
// Mounted once at App root so the queue survives React Router navigation.
// The tray UI (DownloadTray) reads from this context.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { triggerBlobDownload } from "@/features/exports/services/_helpers";

export type DownloadStatus = "queued" | "running" | "complete" | "error" | "cancelled";

export interface DownloadTask {
  id: string;
  label: string;            // shown in the tray, e.g. "Photo bundle — Store #12345"
  filename: string;         // suggested filename for the resulting zip
  status: DownloadStatus;
  progress: number;         // 0-100
  detail?: string;          // free-text status, e.g. "Downloading 42 / 221 photos"
  error?: string;
  blob?: Blob;              // populated when status === "complete"
  createdAt: number;
}

export interface BuildContext {
  signal: AbortSignal;
  setProgress: (pct: number, detail?: string) => void;
}

export interface DownloadSpec {
  label: string;
  filename: string;
  build: (ctx: BuildContext) => Promise<Blob>;
}

interface DownloadManagerValue {
  tasks: DownloadTask[];
  enqueue: (spec: DownloadSpec) => string;
  cancel: (id: string) => void;
  dismiss: (id: string) => void;
  saveAgain: (id: string) => void;
  clearCompleted: () => void;
}

const DownloadManagerContext = createContext<DownloadManagerValue | null>(null);

export function DownloadManagerProvider({ children }: { children: ReactNode }) {
  const [tasks, setTasks] = useState<DownloadTask[]>([]);
  // Keep abort controllers outside of state — they don't render.
  const controllersRef = useRef<Map<string, AbortController>>(new Map());

  const updateTask = useCallback((id: string, patch: Partial<DownloadTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const enqueue = useCallback((spec: DownloadSpec): string => {
    const id = crypto.randomUUID();
    const controller = new AbortController();
    controllersRef.current.set(id, controller);

    const task: DownloadTask = {
      id,
      label: spec.label,
      filename: spec.filename,
      status: "queued",
      progress: 0,
      createdAt: Date.now(),
    };
    setTasks((prev) => [task, ...prev]);

    const ctx: BuildContext = {
      signal: controller.signal,
      setProgress: (pct, detail) => {
        updateTask(id, {
          progress: Math.max(0, Math.min(100, Math.round(pct))),
          detail,
        });
      },
    };

    // Kick off the build asynchronously. We never await it — the tray watches
    // the task's status field.
    (async () => {
      updateTask(id, { status: "running" });
      try {
        const blob = await spec.build(ctx);
        if (controller.signal.aborted) {
          updateTask(id, { status: "cancelled" });
          return;
        }
        updateTask(id, { status: "complete", progress: 100, blob, detail: undefined });
        // Trigger the browser save dialog immediately on completion.
        triggerBlobDownload(blob, spec.filename);
      } catch (err) {
        if (controller.signal.aborted) {
          updateTask(id, { status: "cancelled" });
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        updateTask(id, { status: "error", error: message });
      } finally {
        controllersRef.current.delete(id);
      }
    })();

    return id;
  }, [updateTask]);

  const cancel = useCallback((id: string) => {
    const c = controllersRef.current.get(id);
    if (c) c.abort();
    // The build's finally block will mark the task cancelled. Update
    // immediately too in case the build's not at an abort checkpoint.
    updateTask(id, { status: "cancelled" });
  }, [updateTask]);

  const dismiss = useCallback((id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const saveAgain = useCallback((id: string) => {
    setTasks((prev) => {
      const t = prev.find((x) => x.id === id);
      if (t?.blob) triggerBlobDownload(t.blob, t.filename);
      return prev;
    });
  }, []);

  const clearCompleted = useCallback(() => {
    setTasks((prev) =>
      prev.filter((t) => t.status === "running" || t.status === "queued"),
    );
  }, []);

  // Warn on tab close / refresh / external nav when downloads are in flight.
  const hasActive = tasks.some(
    (t) => t.status === "queued" || t.status === "running",
  );

  useEffect(() => {
    if (!hasActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome ignores custom strings but still shows the prompt as long as
      // returnValue is set to a non-empty string.
      e.returnValue = "A download is still in progress.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasActive]);

  const value = useMemo<DownloadManagerValue>(
    () => ({ tasks, enqueue, cancel, dismiss, saveAgain, clearCompleted }),
    [tasks, enqueue, cancel, dismiss, saveAgain, clearCompleted],
  );

  return (
    <DownloadManagerContext.Provider value={value}>
      {children}
    </DownloadManagerContext.Provider>
  );
}

export function useDownloadManager(): DownloadManagerValue {
  const ctx = useContext(DownloadManagerContext);
  if (!ctx) {
    throw new Error("useDownloadManager must be used inside <DownloadManagerProvider>");
  }
  return ctx;
}
